"use client";

import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createTransfer, getTransfers, receiveTransfer, cancelTransfer, reset } from "@/state/transfers/transfer.slice";
import { getProducts } from "@/state/products/product.slice";
import { getShops } from "@/state/shops/shop.slice";
import { getClientShopScope } from "@/lib/shopScope";
import { useToast } from "@/components/ui/use-toast";
import { usePermission } from "@/hooks/usePermission";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Card } from "@/components/ui/card";
import {
  ArrowsLeftRight,
  Plus,
  Trash,
  MagnifyingGlass,
  Eye,
} from "@phosphor-icons/react";
import { Loader2, ChevronLeft, ChevronRight, Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { useDebounce } from "@/hooks/useDebounce";

export default function TransfersPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { toast } = useToast();
  const canManageStock = usePermission("Stock");

  const { transfers, pagination, isLoading } = useSelector(
    (state) => state.transfers
  );

  // List state
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 400);

  // Detail dialog state
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);

  // Fetch transfers
  useEffect(() => {
    dispatch(getTransfers({ page, limit, search: debouncedSearch }));
  }, [dispatch, page, limit, debouncedSearch]);

  const handleViewTransfer = useCallback((transfer) => {
    setSelectedTransfer(transfer);
    setDetailOpen(true);
  }, []);

  const handleReceive = useCallback(async (id) => {
    try {
      await dispatch(receiveTransfer(id)).unwrap();
      toast({ title: "Received", description: "Stock credited to the destination shop." });
      dispatch(getTransfers({ page, limit, search: debouncedSearch }));
    } catch (e) {
      toast({ variant: "destructive", title: "Error", description: typeof e === "string" ? e : "Failed to receive transfer" });
    }
  }, [dispatch, page, limit, debouncedSearch, toast]);

  const handleCancel = useCallback(async (id) => {
    try {
      await dispatch(cancelTransfer(id)).unwrap();
      toast({ title: "Cancelled", description: "Stock returned to the source shop." });
      dispatch(getTransfers({ page, limit, search: debouncedSearch }));
    } catch (e) {
      toast({ variant: "destructive", title: "Error", description: typeof e === "string" ? e : "Failed to cancel transfer" });
    }
  }, [dispatch, page, limit, debouncedSearch, toast]);

  const statusBadge = (status) => {
    if (status === "received") return <Badge className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">Received</Badge>;
    if (status === "cancelled") return <Badge variant="secondary" className="text-muted-foreground">Cancelled</Badge>;
    return <Badge className="bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">Pending</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="mx-auto p-4 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <ArrowsLeftRight className="h-8 w-8" />
            Stock Transfers
          </h1>
          <p className="text-muted-foreground mt-1">
            Transfer stock between shops
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center space-x-2 bg-white dark:bg-slate-900 px-2 border w-fit">
            <MagnifyingGlass className="w-5 h-5 text-gray-500" />
            <Input
              placeholder="Search by reference..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              className="border-none shadow-none focus-visible:ring-0 w-[200px]"
            />
          </div>
          {canManageStock && (
            <Button
              onClick={() => navigate("/dashboard/inventory/transfers/new")}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              New Transfer
            </Button>
          )}
        </div>
      </div>

      {/* Transfers Table */}
      <Card className="enterprise-table-container">
        <div>
          {isLoading && transfers.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reference</TableHead>
                  <TableHead>From</TableHead>
                  <TableHead>To</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Transferred By</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transfers.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="text-center h-32 text-muted-foreground"
                    >
                      No transfers found.
                    </TableCell>
                  </TableRow>
                ) : (
                  transfers.map((transfer) => (
                    <TableRow key={transfer.id}>
                      <TableCell className="font-mono font-medium">
                        {transfer.reference_number}
                      </TableCell>
                      <TableCell>{transfer.fromShop?.name || "-"}</TableCell>
                      <TableCell>{transfer.toShop?.name || "-"}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">
                          {transfer.items?.length || 0} item(s)
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {transfer.transferredBy?.first_name || transfer.transferredBy?.username || "-"}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(transfer.createdAt)}
                      </TableCell>
                      <TableCell>{statusBadge(transfer.status)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {transfer.status === "pending" && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 text-green-700 border-green-200"
                                onClick={() => handleReceive(transfer.id)}
                                title="Receive at destination"
                              >
                                Receive
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 text-red-600 border-red-200"
                                onClick={() => handleCancel(transfer.id)}
                                title="Cancel and return to source"
                              >
                                Cancel
                              </Button>
                            </>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleViewTransfer(transfer)}
                            title="View details"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </div>
      </Card>

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {pagination.page} of {pagination.pages} ({pagination.total} total)
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= pagination.pages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Transfer Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle className="font-mono">
              {selectedTransfer?.reference_number}
            </DialogTitle>
            <DialogDescription>
              Transfer details
            </DialogDescription>
          </DialogHeader>

          {selectedTransfer && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">From</p>
                  <p className="font-medium">{selectedTransfer.fromShop?.name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">To</p>
                  <p className="font-medium">{selectedTransfer.toShop?.name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Transferred By</p>
                  <p className="font-medium">
                    {selectedTransfer.transferredBy?.first_name
                      ? `${selectedTransfer.transferredBy.first_name} ${selectedTransfer.transferredBy.last_name || ""}`
                      : selectedTransfer.transferredBy?.username || "-"}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Date</p>
                  <p className="font-medium">{formatDate(selectedTransfer.createdAt)}</p>
                </div>
              </div>

              {selectedTransfer.notes && (
                <div className="text-sm">
                  <p className="text-muted-foreground">Notes</p>
                  <p>{selectedTransfer.notes}</p>
                </div>
              )}

              <div className="border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead className="text-right">Quantity</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedTransfer.items?.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="font-medium">
                          {item.Product?.title || `Product #${item.product_id}`}
                        </TableCell>
                        <TableCell className="text-right">{item.quantity}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
